import { useEffect, useRef, useCallback } from 'react';
import { createConsumer } from '@rails/actioncable';
import { buildSubscriptionParams } from '../lib/message_channel_factory';

// メッセージ一覧画面用の WebSocket hook（TGW-1033 / D7）
//
// MessageInboxChannel に購読し、参加者の新着・既読・新規スレッド作成を
// リアルタイムに受信する。N3 多段防御として:
//   A: WS 再接続時の差分 fetch
//   B: window.focus / document.visibilitychange での fetch
//   D: 30 分間隔の defensive refetch
// を内蔵し、silently dead な WS 接続にも備える。
//
// dedup（R2）: 直近 50 件の (target_id, last_message_at) をキャッシュし、
// 同一通知が複数タブから来ても二重描画を避ける。
//
// R5 緩和: companyId が指定されている（コミュニティ別一覧 S2）場合、
// payload の company_id と一致しないものは無視する。
//
// scope フィルタ: 一覧画面ごとに対応する message_group_kind 以外を無視する。
//
// @param {Object} args
// @param {number}   args.currentUserId    ログイン中ユーザーID（マルチタブ判定用、未使用でも将来拡張用に受ける）
// @param {*}        args.companyId        コミュニティID。S1 (DM一覧) の場合は null/undefined
// @param {string}   args.scope            'dm' | 'circle' | 'event' | 'unread'
// @param {Function} args.onMessageReceived  payload => void  F1〜F3
// @param {Function} args.onUnreadCountChanged payload => void F4
// @param {Function} args.onNewThread       payload => void F6
// @param {Function} args.refetch           () => Promise<void>  N3-A/B/D で fetch を発火させる関数
const DEFENSIVE_REFETCH_MS = 30 * 60 * 1000; // 30 分
const SEEN_CACHE_LIMIT = 50;

function payloadMatchesScope(payload, scope) {
  const kind = payload && payload.message_group_kind;
  if (!kind) return false;
  switch (scope) {
    case 'dm':
      return kind === 'room';
    case 'circle':
      return kind === 'circle';
    case 'event':
      return kind === 'event';
    case 'unread':
      return kind === 'room' || kind === 'circle' || kind === 'event';
    default:
      return false;
  }
}

function payloadMatchesCompany(payload, companyId) {
  // companyId 未指定（DM 一覧 S1 等）→ company_id がない broadcast のみ受け入れる
  if (companyId === null || typeof companyId === 'undefined') {
    return (
      payload.company_id === null || typeof payload.company_id === 'undefined'
    );
  }
  // companyId 指定あり（コミュニティ別 S2）→ 一致するもののみ
  // NOTE: companyId は通常 hashid 文字列、payload.company_id は integer。
  //       String 化して比較する。完全一致しない場合は次回 fetch で吸収する（R5 緩和）。
  if (
    payload.company_id === null ||
    typeof payload.company_id === 'undefined'
  ) {
    return false;
  }
  return String(payload.company_id) === String(companyId);
}

export default function useInboxChannel({
  currentUserId,
  companyId,
  scope,
  onMessageReceived,
  onUnreadCountChanged,
  onNewThread,
  refetch,
}) {
  const consumerRef = useRef(null);
  const subscriptionRef = useRef(null);
  const wasDisconnectedRef = useRef(false);
  const seenRef = useRef([]);
  const refetchRef = useRef(refetch);
  const onMessageReceivedRef = useRef(onMessageReceived);
  const onUnreadCountChangedRef = useRef(onUnreadCountChanged);
  const onNewThreadRef = useRef(onNewThread);
  const companyIdRef = useRef(companyId);
  const scopeRef = useRef(scope);

  // 最新の callback / props を ref に保持（hook の再 subscribe を避ける）
  useEffect(() => {
    refetchRef.current = refetch;
    onMessageReceivedRef.current = onMessageReceived;
    onUnreadCountChangedRef.current = onUnreadCountChanged;
    onNewThreadRef.current = onNewThread;
    companyIdRef.current = companyId;
    scopeRef.current = scope;
  }, [
    refetch,
    onMessageReceived,
    onUnreadCountChanged,
    onNewThread,
    companyId,
    scope,
  ]);

  const safeRefetch = useCallback(() => {
    const fn = refetchRef.current;
    if (typeof fn !== 'function') return undefined;
    try {
      const ret = fn();
      // Promise でも同期関数でも握り潰す
      if (ret && typeof ret.catch === 'function') {
        ret.catch((e) => {
          // eslint-disable-next-line no-console
          console.warn('[useInboxChannel] refetch failed:', e);
        });
      }
      return ret;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[useInboxChannel] refetch threw:', e);
      return undefined;
    }
  }, []);

  // 直近 N 件と同じなら true（dedup, R2）
  const isDuplicate = useCallback((payload) => {
    if (!payload || typeof payload.target_id === 'undefined') return false;
    const key = `${payload.message_group_kind}_${payload.target_id}_${payload.last_message_at || ''}`;
    if (seenRef.current.includes(key)) return true;
    seenRef.current.push(key);
    if (seenRef.current.length > SEEN_CACHE_LIMIT) {
      seenRef.current.shift();
    }
    return false;
  }, []);

  // WebSocket 購読
  useEffect(() => {
    const params = buildSubscriptionParams('inbox', null);
    const consumer = createConsumer();
    consumerRef.current = consumer;

    subscriptionRef.current = consumer.subscriptions.create(params, {
      connected: () => {
        if (wasDisconnectedRef.current) {
          safeRefetch();
        }
        wasDisconnectedRef.current = false;
      },
      disconnected: () => {
        wasDisconnectedRef.current = true;
      },
      received: (data) => {
        if (!data || typeof data !== 'object') return;

        // scope フィルタ
        if (
          data.type === 'inbox_message_received' ||
          data.type === 'inbox_unread_count_changed'
        ) {
          if (!payloadMatchesScope(data, scopeRef.current)) return;
          if (!payloadMatchesCompany(data, companyIdRef.current)) return;
        }

        if (data.type === 'inbox_message_received') {
          // dedup は通常メッセ受信のみ（既読変更は連続発火する想定）
          if (isDuplicate(data)) return;
          if (data.is_new_thread) {
            const fn = onNewThreadRef.current;
            if (typeof fn === 'function') fn(data);
          } else {
            const fn = onMessageReceivedRef.current;
            if (typeof fn === 'function') fn(data);
          }
        } else if (data.type === 'inbox_unread_count_changed') {
          const fn = onUnreadCountChangedRef.current;
          if (typeof fn === 'function') fn(data);
        }
      },
    });

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
      if (consumerRef.current) {
        consumerRef.current.disconnect();
        consumerRef.current = null;
      }
    };
    // 一度だけ購読する。callback / props の変更は ref 経由で反映する
  }, [safeRefetch, isDuplicate]);

  // N3-B: focus / visibilitychange による fetch 補完
  useEffect(() => {
    const handleFocus = () => {
      safeRefetch();
    };
    const handleVisibility = () => {
      if (
        typeof document !== 'undefined' &&
        document.visibilityState === 'visible'
      ) {
        safeRefetch();
      }
    };
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [safeRefetch]);

  // N3-D: 30 分間隔の defensive refetch
  useEffect(() => {
    const intervalId = setInterval(safeRefetch, DEFENSIVE_REFETCH_MS);
    return () => clearInterval(intervalId);
  }, [safeRefetch]);

  // currentUserId は将来のマルチタブ最適化用に受け取るのみ（現状は使用しない）
  // eslint-disable-next-line no-unused-expressions
  currentUserId;
}
